// eslint-disable-next-line import/no-internal-modules
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { IFileAttachment, IImpersonationContext, IPostTarget, SocialMediaType } from '../../../../extViewmodels';
import {
	getContentLength,
	getShortenedString,
	joinContentWithSignature,
	parseFacebookMentions,
	sortPlatformTargets,
} from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSocialMediaDisclosure } from '../../../../queries/Social/useSocialMediaDisclosure';
import { useSocialMediaSignature } from '../../../../queries/Templates/useTemplate';
import { Pill } from '../../../components-v2/Pill';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Tooltip';
import { CheckmarkIcon } from '../../svgs/icons/CheckmarkIcon';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { charmGray } from '../../../styles/colors';
import { SocialMediaImagesPreviewModal } from '../SocialMediaImagesPreviewModal';
import { PreviewImages, PreviewPostTargets } from './presentation';
import { styleSheet } from './styles';

/** What one platform publishes. `postContent` is stored content, mention tokens included. */
export interface IPreviewPlatformVersion {
	postContent?: string;
	postImages?: IFileAttachment[];
}

interface IProps {
	className?: string;
	postTargets: IPostTarget[];
	styles?: StyleDeclarationValue[];
	overlayLogo?: boolean;
	/** Stored content; a Facebook Page mention is its {{@Page Name|pageId}} token. */
	postContent?: string;
	postImages?: IFileAttachment[];
	impersonationCtx: IImpersonationContext;
	hideWhiteBackground?: boolean;
	postCreatorUserId?: string;
	logoSize?: number;
	onLogoSizeChange?: (size: number) => void;
	templateId?: string;
	/** Drives the preview from outside, so the platform tabs and the preview show the same version. */
	selectedProvider?: SocialMediaType;
	onSelectedProviderChange?: (provider: SocialMediaType) => void;
	/**
	 * Set for a post split by platform: each platform previews, and holds space for, its own version, and
	 * `postContent` and `postImages` go unused. Unset, every platform shares those two.
	 */
	getPlatformVersion?: (provider: SocialMediaType) => IPreviewPlatformVersion;
}

const collapseContent = (content: string | undefined, displayLength: number) =>
	content?.length > displayLength ? getShortenedString(content.trim(), displayLength).trim() : content?.trim();

const toPreviewCard = (version: IPreviewPlatformVersion, provider: SocialMediaType, signatureContent: string) => {
	// The stored body carries its mentions as tokens, so they come out before anything measures or
	// truncates the caption.
	const { content, mentions } = parseFacebookMentions(version.postContent);
	// The signature publishes as part of the caption, so it counts against the platform's cut-off: fold it
	// into the previewed content instead of listing it in full below an already truncated post.
	const contentWithSignature = joinContentWithSignature(content, signatureContent);
	const contentDisplayLength = getContentLength(provider);
	return {
		contentDisplayLength,
		displayContent: collapseContent(contentWithSignature, contentDisplayLength),
		facebookMentions: mentions,
		postContent: contentWithSignature,
		postImages: version.postImages,
	};
};

export const PreviewSocialMediaPost = ({
	className = '',
	children,
	styles = [],
	postTargets,
	overlayLogo,
	postContent,
	postImages,
	impersonationCtx,
	hideWhiteBackground,
	postCreatorUserId,
	logoSize,
	onLogoSizeChange,
	templateId,
	selectedProvider,
	onSelectedProviderChange,
	getPlatformVersion,
}: React.PropsWithChildren<IProps>) => {
	const [initialImageUrl, setInitialImageUrl] = React.useState<string>('');
	const [imagePreviewModalOpen, setImagePreviewModalOpen] = React.useState(false);
	const userSession = useUserSession();
	const account = impersonationCtx?.account || userSession.account;
	const logoUrl = account?.preferences?.logo?.url;

	// Load the signature template using the new endpoint
	const shouldUseForUserId = !impersonationCtx && postCreatorUserId && userSession.user.id !== postCreatorUserId;
	const { data: signatureTemplate } = useSocialMediaSignature({
		forUserId: shouldUseForUserId ? postCreatorUserId : undefined,
		postCreatorUserId,
		impersonationContext: impersonationCtx,
		enabled: true,
	});

	const { data: disclosureContent } = useSocialMediaDisclosure({
		impersonationContext: impersonationCtx,
		templateId,
	});

	// Extract signature content
	const signatureContent = React.useMemo(() => {
		// Return empty if no template exists
		if (!signatureTemplate?.content?.document) return '';

		try {
			// Extract content from <lev-content> wrapper
			const parser = new DOMParser();
			const doc = parser.parseFromString(signatureTemplate.content.document, 'text/html');
			const levContentElement = doc.querySelector('lev-content');
			return levContentElement?.textContent || signatureTemplate.content.document;
		} catch (error) {
			console.error('Failed to parse signature content:', error);
			return '';
		}
	}, [signatureTemplate]);

	const onClickEnlargeImage = (initialUrl: string) => {
		setInitialImageUrl(initialUrl);
		setImagePreviewModalOpen(true);
	};

	// One ordered list drives the icon row, the selected index and the rendered body, so a click on a
	// platform icon cannot select a different platform's preview.
	const orderedTargets = React.useMemo(() => sortPlatformTargets([...(postTargets ?? [])]), [postTargets]);
	const [internalTargetIndex, setInternalTargetIndex] = React.useState(0);
	const isProviderControlled = !!selectedProvider && !!onSelectedProviderChange;
	const targetIndex = isProviderControlled
		? Math.max(
				0,
				orderedTargets.findIndex(target => target.provider === selectedProvider)
			)
		: internalTargetIndex;
	const onSetTargetPreview = (index?: number) => () => {
		if (isProviderControlled) {
			const provider = orderedTargets[index]?.provider;
			if (provider) {
				onSelectedProviderChange(provider);
			}
			return;
		}
		setInternalTargetIndex(index);
	};

	// Update target index back to zero when the targets input has changed (when they are desellected)
	React.useEffect(() => {
		setInternalTargetIndex(0);
	}, [postTargets]);

	const shownProvider = orderedTargets[targetIndex]?.provider;
	const shownCard = toPreviewCard(
		getPlatformVersion && shownProvider ? getPlatformVersion(shownProvider) : { postContent, postImages },
		shownProvider,
		signatureContent
	);

	// Each platform cuts the caption at a different length. Reserving the height of the one that shows the
	// most keeps the card - and everything under it - still while the reader clicks through the platforms.
	// A split post holds that space with each platform's own card below instead.
	const displayLengths = orderedTargets.map(target => getContentLength(target.provider));
	const longestDisplayLength = displayLengths.length ? Math.max(...displayLengths) : shownCard.contentDisplayLength;
	const sizerContent =
		!getPlatformVersion && longestDisplayLength > shownCard.contentDisplayLength
			? collapseContent(shownCard.postContent, longestDisplayLength)
			: undefined;

	// Each platform arranges the media at its own height, so the card is measured against every platform in
	// the post and the extra space is left below it. A video shared by every platform looks the same on
	// each, so it needs none; a split post's versions differ in text and media alike, so each one gets a layer.
	const hasSharedVideo = !getPlatformVersion && postImages?.some(image => image.mimeType?.startsWith('video/'));
	const needsCardSizers = !!getPlatformVersion || (!!postImages?.length && !hasSharedVideo);
	const cardSizerTargets: IPostTarget[] = [];
	if (needsCardSizers) {
		for (const target of orderedTargets) {
			const isShown = target.provider === shownProvider;
			const isDuplicate = cardSizerTargets.some(sizerTarget => sizerTarget.provider === target.provider);
			if (!isShown && !isDuplicate) {
				cardSizerTargets.push(target);
			}
		}
	}

	if (!postTargets) {
		return null;
	}

	const previewCardProps = {
		...shownCard,
		disclosureContent,
		onClickEnlargeImage,
		sizerContent,
		sizerTruncated: shownCard.postContent?.length > longestDisplayLength,
	};
	const getSizerCardProps = (provider: SocialMediaType) =>
		getPlatformVersion
			? {
					...toPreviewCard(getPlatformVersion(provider), provider, signatureContent),
					disclosureContent,
					onClickEnlargeImage,
				}
			: previewCardProps;

	return (
		<div className={`preview-social-media-post ${className} ${css(styleSheet.previewContentContainer, ...styles)}`}>
			<section className={css(styleSheet.previewContentSection)}>
				{signatureContent ? (
					<div className={css(styleSheet.signatureBadge)} data-testid='signature-applied-badge'>
						<Pill color='green' icon={<CheckmarkIcon fillColor='currentColor' />} label='Signature' variant='label' />
					</div>
				) : null}
				<div className={css(styleSheet.previewHeaderRow)}>
					<h3 className={css(styleSheet.previewHeader)}>PREVIEW POST</h3>
					{/* Its own provider: the admin app renders this preview without one above it. */}
					<TooltipProvider>
						<Tooltip delayDuration={0}>
							<TooltipTrigger aria-label='About this preview' className={css(styleSheet.previewDisclaimerTrigger)}>
								<InfoIcon fillColor={charmGray} />
							</TooltipTrigger>
							<TooltipContent side='bottom' styles={[styleSheet.previewDisclaimerTooltip]}>
								This is only a preview. Content may display slightly differently when published.
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
				<PreviewPostTargets
					postTargets={orderedTargets}
					onSetTargetPreview={onSetTargetPreview}
					targetIndex={targetIndex}
				/>{' '}
				<div className={css(styleSheet.previewCardArea)}>
					<div className={css(styleSheet.previewCardLayer)}>
						<PreviewImages {...previewCardProps} postTargets={orderedTargets} targetIndex={targetIndex} />
					</div>
					{cardSizerTargets.map(target => (
						<div aria-hidden className={css(styleSheet.previewCardSizer)} key={`sizer-${target.provider}`}>
							<PreviewImages {...getSizerCardProps(target.provider)} isSizer postTargets={[target]} targetIndex={0} />
						</div>
					))}
				</div>
			</section>
			{children}
			<SocialMediaImagesPreviewModal
				modalProps={{
					isOpen: imagePreviewModalOpen,
					onRequestClose: () => setImagePreviewModalOpen(false),
				}}
				initialImageUrl={initialImageUrl}
				images={shownCard.postImages}
				showOverlayLogo={overlayLogo && !shownCard.postImages?.every(x => x.hasLogoOverlay)}
				logoUrl={logoUrl}
				hideWhiteBackground={hideWhiteBackground}
				logoSize={logoSize}
				onLogoSizeChange={onLogoSizeChange}
			/>
		</div>
	);
};
